import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, Clock, AlertTriangle, TrendingUp, FileText } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AreaChart, BarChart, PieChart, ChartContainer } from '@/components/charts';
import { MetricCard } from '@/components/MetricCard';
import { formatCurrency } from '@/lib/utils';
import { fetchQboInvoices } from '../api';
import type { QboInvoice } from '../api';
import type { ChartDataPoint } from '@/components/charts/types';

// =============================================================================
// Helpers
// =============================================================================

const STATUS_COLORS: Record<string, string> = {
  Open: '#3b82f6',
  Partial: '#f59e0b',
  Paid: '#22c55e',
  Voided: '#ef4444',
};

function daysBetween(from: string, to: string): number {
  return Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000);
}

function getMonthKey(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function getAgingBucket(daysOverdue: number): string {
  if (daysOverdue <= 30) return '0-30';
  if (daysOverdue <= 60) return '31-60';
  if (daysOverdue <= 90) return '61-90';
  return '90+';
}

// =============================================================================
// Data fetching hook — loads ALL QBO invoices for analysis
// =============================================================================

function useAllInvoices(dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: ['analysis-qbo-invoices', dateFrom, dateTo],
    queryFn: async () => {
      const res = await fetchQboInvoices({
        start: dateFrom,
        end: dateTo,
      });
      return res.invoices;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Revenue Sub-tab
// =============================================================================

function RevenueView({ invoices, loading }: { invoices: QboInvoice[]; loading: boolean }) {
  const stats = useMemo(() => {
    const totalInvoiced = invoices.reduce((s, i) => s + i.total, 0);
    const totalPaid = invoices.reduce((s, i) => s + i.paid, 0);
    const totalOutstanding = invoices.reduce((s, i) => s + i.balance, 0);
    const today = new Date().toISOString().slice(0, 10);
    const totalOverdue = invoices
      .filter((i) => i.balance > 0 && i.dueDate < today)
      .reduce((s, i) => s + i.balance, 0);
    return { totalInvoiced, totalPaid, totalOutstanding, totalOverdue };
  }, [invoices]);

  // Monthly revenue trend
  const monthlyData = useMemo<ChartDataPoint[]>(() => {
    const months = new Map<string, { invoiced: number; paid: number }>();
    for (const inv of invoices) {
      if (!inv.date) continue;
      const key = getMonthKey(inv.date);
      const entry = months.get(key) || { invoiced: 0, paid: 0 };
      entry.invoiced += inv.total;
      entry.paid += inv.paid;
      months.set(key, entry);
    }
    return [...months.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, { invoiced, paid }]) => ({
        name,
        invoiced: Math.round(invoiced),
        paid: Math.round(paid),
      }));
  }, [invoices]);

  // Status distribution pie
  const statusData = useMemo<ChartDataPoint[]>(() => {
    const byStatus = new Map<string, number>();
    for (const inv of invoices) {
      byStatus.set(inv.status, (byStatus.get(inv.status) || 0) + inv.total);
    }
    return [...byStatus.entries()].map(([name, value]) => ({
      name,
      value: Math.round(value),
    }));
  }, [invoices]);

  // Currency breakdown
  const currencyData = useMemo<ChartDataPoint[]>(() => {
    const byCurrency = new Map<string, number>();
    for (const inv of invoices) {
      byCurrency.set(inv.currency || 'CAD', (byCurrency.get(inv.currency || 'CAD') || 0) + inv.total);
    }
    return [...byCurrency.entries()].map(([name, value]) => ({
      name,
      value: Math.round(value),
    }));
  }, [invoices]);

  return (
    <div className="space-y-6 min-w-0">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title="Total Invoiced" value={formatCurrency(stats.totalInvoiced)} icon={<DollarSign className="h-4 w-4" />} color="blue" loading={loading} />
        <MetricCard title="Total Paid" value={formatCurrency(stats.totalPaid)} icon={<TrendingUp className="h-4 w-4" />} color="green" loading={loading} />
        <MetricCard title="Outstanding" value={formatCurrency(stats.totalOutstanding)} icon={<Clock className="h-4 w-4" />} color="orange" loading={loading} />
        <MetricCard title="Overdue" value={formatCurrency(stats.totalOverdue)} icon={<AlertTriangle className="h-4 w-4" />} color="red" loading={loading} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
        <ChartContainer title="Monthly Revenue Trend" description="Invoiced vs Paid" loading={loading}>
          <AreaChart
            data={monthlyData}
            series={[
              { dataKey: 'invoiced', name: 'Invoiced', color: '#3b82f6' },
              { dataKey: 'paid', name: 'Paid', color: '#22c55e' },
            ]}
            xAxisKey="name"
            height={300}
            showGrid
            showLegend
            stacked={false}
          />
        </ChartContainer>

        <ChartContainer title="Revenue by Status" loading={loading}>
          <PieChart
            data={statusData}
            height={300}
            donut
            showLegend
            colors={Object.values(STATUS_COLORS)}
          />
        </ChartContainer>

        {currencyData.length > 1 && (
          <ChartContainer title="Revenue by Currency" loading={loading}>
            <BarChart data={currencyData} height={300} showGrid />
          </ChartContainer>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Operations Sub-tab
// =============================================================================

function OperationsView({ invoices, loading }: { invoices: QboInvoice[]; loading: boolean }) {
  const today = new Date().toISOString().split('T')[0];

  const stats = useMemo(() => {
    // Avg days to payment: for paid invoices, use date → updatedAt as proxy
    const paidInvoices = invoices.filter((i) => i.status === 'Paid' && i.date && i.updatedAt);
    const avgDaysToPayment = paidInvoices.length
      ? Math.round(paidInvoices.reduce((s, i) => s + daysBetween(i.date, i.updatedAt.slice(0, 10)), 0) / paidInvoices.length)
      : 0;

    const overdue = invoices.filter((i) => i.balance > 0 && i.dueDate < today);
    const overdueCount = overdue.length;
    const overdueAmount = overdue.reduce((s, i) => s + i.balance, 0);
    const avgInvoiceSize = invoices.length
      ? Math.round(invoices.reduce((s, i) => s + i.total, 0) / invoices.length)
      : 0;

    return { avgDaysToPayment, overdueCount, overdueAmount, avgInvoiceSize };
  }, [invoices, today]);

  // Aging buckets
  const agingData = useMemo<ChartDataPoint[]>(() => {
    const buckets = new Map<string, number>([
      ['0-30', 0],
      ['31-60', 0],
      ['61-90', 0],
      ['90+', 0],
    ]);
    for (const inv of invoices) {
      if (inv.balance <= 0 || !inv.dueDate) continue;
      const daysOverdue = daysBetween(inv.dueDate, today);
      if (daysOverdue < 0) continue;
      const bucket = getAgingBucket(daysOverdue);
      buckets.set(bucket, (buckets.get(bucket) || 0) + inv.balance);
    }
    return [...buckets.entries()].map(([name, value]) => ({
      name: `${name} days`,
      value: Math.round(value),
    }));
  }, [invoices, today]);

  // Monthly invoice count
  const monthlyCountData = useMemo<ChartDataPoint[]>(() => {
    const months = new Map<string, number>();
    for (const inv of invoices) {
      if (!inv.date) continue;
      const key = getMonthKey(inv.date);
      months.set(key, (months.get(key) || 0) + 1);
    }
    return [...months.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, value]) => ({ name, value }));
  }, [invoices]);

  // Overdue invoices table
  const overdueInvoices = useMemo(() => {
    return invoices
      .filter((i) => i.balance > 0 && i.dueDate < today)
      .map((i) => ({
        ...i,
        daysOverdue: daysBetween(i.dueDate, today),
      }))
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [invoices, today]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title="Avg Days to Payment" value={stats.avgDaysToPayment} suffix="days" icon={<Clock className="h-4 w-4" />} color="blue" loading={loading} />
        <MetricCard title="Overdue Count" value={stats.overdueCount} icon={<AlertTriangle className="h-4 w-4" />} color="red" loading={loading} />
        <MetricCard title="Overdue Amount" value={formatCurrency(stats.overdueAmount)} icon={<DollarSign className="h-4 w-4" />} color="red" loading={loading} />
        <MetricCard title="Avg Invoice Size" value={formatCurrency(stats.avgInvoiceSize)} icon={<FileText className="h-4 w-4" />} color="purple" loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
        <ChartContainer title="Invoice Aging" description="Outstanding by days overdue" loading={loading}>
          <BarChart data={agingData} height={300} showGrid colorByValue colors={['#22c55e', '#f59e0b', '#f97316', '#ef4444']} />
        </ChartContainer>

        <ChartContainer title="Monthly Invoice Count" loading={loading}>
          <BarChart data={monthlyCountData} height={300} showGrid />
        </ChartContainer>
      </div>

      {/* Overdue invoices table */}
      {overdueInvoices.length > 0 && (
        <ChartContainer title="Overdue Invoices" description={`${overdueInvoices.length} invoices past due`} loading={loading} height="auto">
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Days Overdue</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overdueInvoices.slice(0, 20).map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.docNumber}</TableCell>
                    <TableCell>{inv.customerName}</TableCell>
                    <TableCell>{inv.dueDate}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={inv.daysOverdue > 60 ? 'destructive' : 'warning'}>
                        {inv.daysOverdue}d
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(inv.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </ChartContainer>
      )}
    </div>
  );
}

// =============================================================================
// Accounts Sub-tab
// =============================================================================

interface AccountSummary {
  name: string;
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  invoiceCount: number;
}

function AccountsView({ invoices, loading }: { invoices: QboInvoice[]; loading: boolean }) {
  const [sortKey, setSortKey] = useState<keyof AccountSummary>('totalInvoiced');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);

  const accountSummaries = useMemo<AccountSummary[]>(() => {
    const map = new Map<string, AccountSummary>();
    for (const inv of invoices) {
      const name = inv.customerName || 'Unknown';
      const entry = map.get(name) || { name, totalInvoiced: 0, totalPaid: 0, totalOutstanding: 0, invoiceCount: 0 };
      entry.totalInvoiced += inv.total;
      entry.totalPaid += inv.paid;
      entry.totalOutstanding += inv.balance;
      entry.invoiceCount++;
      map.set(name, entry);
    }
    return [...map.values()];
  }, [invoices]);

  const sorted = useMemo(() => {
    return [...accountSummaries].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'number' && typeof bVal === 'number') return (aVal - bVal) * sortDir;
      return String(aVal).localeCompare(String(bVal)) * sortDir;
    });
  }, [accountSummaries, sortKey, sortDir]);

  const handleSort = (key: keyof AccountSummary) => {
    if (key === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDir(-1); }
  };

  // Monthly revenue pivot: customer × month
  const { months, pivotRows, monthTotals, grandTotal } = useMemo(() => {
    const monthSet = new Set<string>();
    const accountMonthMap = new Map<string, Map<string, number>>();

    for (const inv of invoices) {
      if (!inv.date) continue;
      const month = getMonthKey(inv.date);
      const account = inv.customerName || 'Unknown';
      monthSet.add(month);

      if (!accountMonthMap.has(account)) accountMonthMap.set(account, new Map());
      const am = accountMonthMap.get(account)!;
      am.set(month, (am.get(month) || 0) + inv.total);
    }

    const sortedMonths = [...monthSet].sort();
    const rows = [...accountMonthMap.entries()]
      .map(([account, monthMap]) => {
        const total = [...monthMap.values()].reduce((s, v) => s + v, 0);
        return { account, monthMap, total };
      })
      .sort((a, b) => b.total - a.total);

    const mTotals = new Map<string, number>();
    let gTotal = 0;
    for (const m of sortedMonths) {
      const colTotal = rows.reduce((s, r) => s + (r.monthMap.get(m) || 0), 0);
      mTotals.set(m, colTotal);
      gTotal += colTotal;
    }

    return { months: sortedMonths, pivotRows: rows, monthTotals: mTotals, grandTotal: gTotal };
  }, [invoices]);

  // Top 10 by revenue
  const top10Revenue = useMemo<ChartDataPoint[]>(() => {
    return [...accountSummaries]
      .sort((a, b) => b.totalInvoiced - a.totalInvoiced)
      .slice(0, 10)
      .map((a) => ({ name: a.name, value: Math.round(a.totalInvoiced) }));
  }, [accountSummaries]);

  return (
    <div className="space-y-6">
      <ChartContainer title="Top 10 Customers by Revenue" loading={loading}>
        <BarChart data={top10Revenue} height={350} horizontal showGrid />
      </ChartContainer>

      {/* Account summary table */}
      <ChartContainer title="Customer Summary" description={`${accountSummaries.length} customers`} loading={loading} height="auto">
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                  Customer {sortKey === 'name' && (sortDir === 1 ? '↑' : '↓')}
                </TableHead>
                <TableHead className="text-right cursor-pointer" onClick={() => handleSort('invoiceCount')}>
                  Invoices {sortKey === 'invoiceCount' && (sortDir === 1 ? '↑' : '↓')}
                </TableHead>
                <TableHead className="text-right cursor-pointer" onClick={() => handleSort('totalInvoiced')}>
                  Total {sortKey === 'totalInvoiced' && (sortDir === 1 ? '↑' : '↓')}
                </TableHead>
                <TableHead className="text-right cursor-pointer" onClick={() => handleSort('totalPaid')}>
                  Paid {sortKey === 'totalPaid' && (sortDir === 1 ? '↑' : '↓')}
                </TableHead>
                <TableHead className="text-right cursor-pointer" onClick={() => handleSort('totalOutstanding')}>
                  Outstanding {sortKey === 'totalOutstanding' && (sortDir === 1 ? '↑' : '↓')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}
              {!loading && sorted.map((acc) => (
                <TableRow key={acc.name}>
                  <TableCell className="font-medium">{acc.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{acc.invoiceCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(acc.totalInvoiced)}</TableCell>
                  <TableCell className="text-right tabular-nums text-green-600">{formatCurrency(acc.totalPaid)}</TableCell>
                  <TableCell className="text-right tabular-nums text-blue-600">{formatCurrency(acc.totalOutstanding)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </ChartContainer>

      {/* Monthly revenue pivot table */}
      <ChartContainer title="Monthly Revenue by Customer" description={`${pivotRows.length} customers × ${months.length} months`} loading={loading} height="auto">
        <div className="border rounded-lg overflow-auto max-h-[600px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-background z-10 min-w-[200px]">Customer</TableHead>
                {months.map((m) => (
                  <TableHead key={m} className="text-right min-w-[100px] text-xs">{m}</TableHead>
                ))}
                <TableHead className="text-right min-w-[110px] font-bold">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  {months.map((m) => (
                    <TableCell key={m}><Skeleton className="h-4 w-16" /></TableCell>
                  ))}
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                </TableRow>
              ))}
              {!loading && pivotRows.map((row) => (
                <TableRow key={row.account}>
                  <TableCell className="sticky left-0 bg-background z-10 font-medium text-sm">{row.account}</TableCell>
                  {months.map((m) => {
                    const val = row.monthMap.get(m) || 0;
                    return (
                      <TableCell key={m} className="text-right tabular-nums text-sm">
                        {val > 0 ? formatCurrency(val) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-right tabular-nums font-semibold text-sm">{formatCurrency(row.total)}</TableCell>
                </TableRow>
              ))}
              {/* Totals row */}
              {!loading && pivotRows.length > 0 && (
                <TableRow className="bg-muted/50 font-semibold">
                  <TableCell className="sticky left-0 bg-muted/50 z-10">Total</TableCell>
                  {months.map((m) => (
                    <TableCell key={m} className="text-right tabular-nums text-sm">
                      {formatCurrency(monthTotals.get(m) || 0)}
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums text-sm">{formatCurrency(grandTotal)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </ChartContainer>
    </div>
  );
}

// =============================================================================
// Main AnalysisTab
// =============================================================================

export default function AnalysisTab() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: invoices = [], isLoading } = useAllInvoices(
    dateFrom || undefined,
    dateTo || undefined,
  );

  return (
    <div className="space-y-4 min-w-0">
      {/* Date range filter */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">From</label>
          <Input
            type="date"
            className="w-40"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">To</label>
          <Input
            type="date"
            className="w-40"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        {(dateFrom || dateTo) && (
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => { setDateFrom(''); setDateTo(''); }}
          >
            Clear
          </button>
        )}
        <span className="text-sm text-muted-foreground">
          {isLoading ? 'Loading...' : `${invoices.length} invoices`}
        </span>
      </div>

      {/* Sub-tabs */}
      <Tabs defaultValue="revenue">
        <TabsList>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
        </TabsList>
        <TabsContent value="revenue" className="mt-4">
          <RevenueView invoices={invoices} loading={isLoading} />
        </TabsContent>
        <TabsContent value="operations" className="mt-4">
          <OperationsView invoices={invoices} loading={isLoading} />
        </TabsContent>
        <TabsContent value="accounts" className="mt-4">
          <AccountsView invoices={invoices} loading={isLoading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
